<template>
  <div class="document-actions d-flex align-center">
    <!-- Context: where this document came from, and where it stands -->
    <div class="d-flex align-center mr-4">
      <v-btn
        v-if="orderBookingId"
        :to="{ name: 'orders.single', params: {b_id: orderBookingId} }"
        class="px-1 text-none"
        prepend-icon="mdi-chevron-left"
        size="small"
        variant="text"
      >
        Order {{ orderBookingId }}
      </v-btn>
      <v-chip
        v-if="statusChip"
        :color="statusChip.color"
        class="ml-1"
        size="small"
        variant="tonal"
      >
        {{ statusChip.label }}
      </v-chip>
    </div>

    <template v-if="device !== 'mobile'">
      <v-btn
        v-if="hasCustomer"
        class="mr-2"
        prepend-icon="mdi-send"
        variant="tonal"
        @click="sendDialogRef?.openDialog()"
      >
        Send
      </v-btn>

      <!-- Download, with Print tucked into the split -->
      <div class="d-inline-flex mr-2">
        <v-btn
          :color="documentCanBeFinalized ? undefined : 'primary'"
          :variant="documentCanBeFinalized ? 'tonal' : 'flat'"
          class="rounded-e-0"
          prepend-icon="mdi-download"
          @click="downloadOrderDocumentInit()"
        >
          Download
        </v-btn>
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn
              :color="documentCanBeFinalized ? undefined : 'primary'"
              :variant="documentCanBeFinalized ? 'tonal' : 'flat'"
              aria-label="More PDF options"
              class="rounded-s-0 px-0"
              min-width="32"
              v-bind="props"
            >
              <v-icon size="small">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item prepend-icon="mdi-printer" title="Print" @click="printElem()"/>
          </v-list>
        </v-menu>
      </div>

      <v-btn
        v-if="documentCanBeFinalized"
        :loading="orderLoading.update"
        class="mr-2"
        color="primary"
        prepend-icon="mdi-check"
        variant="flat"
        @click="finalizeDocumentInit()"
      >
        Finalize
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn aria-label="More actions" icon="mdi-dots-horizontal" size="small" v-bind="props" variant="text"/>
        </template>
        <v-list density="compact">
          <v-list-item
            v-if="documentType === 'Document::Invoice'"
            prepend-icon="mdi-link"
            title="Copy public link"
            @click="copyPublicLink()"
          />
          <v-list-item
            v-if="documentType === 'Document::Quote'"
            prepend-icon="mdi-file-swap-outline"
            title="Convert to invoice"
            @click="convertDialog = true"
          />
          <v-divider class="my-1"/>
          <v-list-item
            base-color="error"
            prepend-icon="mdi-delete-outline"
            title="Delete document"
            @click="deleteOrderDocumentInit()"
          />
        </v-list>
      </v-menu>
    </template>

    <v-menu v-else>
      <template v-slot:activator="{ props }">
        <v-btn :rounded="true" aria-label="Document actions" class="ml-1" v-bind="props" variant="outlined">
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
      <v-list density="compact">
        <v-list-item v-if="hasCustomer" prepend-icon="mdi-send" title="Send to customer" @click="sendDialogRef?.openDialog()"/>
        <v-list-item prepend-icon="mdi-download" title="Download" @click="downloadOrderDocumentInit()"/>
        <v-list-item prepend-icon="mdi-printer" title="Print" @click="printElem()"/>
        <v-list-item
          v-if="documentCanBeFinalized"
          prepend-icon="mdi-check"
          title="Finalize"
          @click="finalizeDocumentInit()"
        />
        <v-list-item
          v-if="documentType === 'Document::Invoice'"
          prepend-icon="mdi-link"
          title="Copy public link"
          @click="copyPublicLink()"
        />
        <v-list-item
          v-if="documentType === 'Document::Quote'"
          prepend-icon="mdi-file-swap-outline"
          title="Convert to invoice"
          @click="convertDialog = true"
        />
        <v-divider class="my-1"/>
        <v-list-item
          base-color="error"
          prepend-icon="mdi-delete-outline"
          title="Delete document"
          @click="deleteOrderDocumentInit()"
        />
      </v-list>
    </v-menu>

    <v-dialog v-model="convertDialog" max-width="420">
      <v-card :disabled="converting">
        <v-card-title>Convert to invoice</v-card-title>
        <v-card-text>
          This quote becomes an invoice with the next invoice number and your invoice defaults. The quote number is not
          kept.
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn size="small" variant="text" @click="convertDialog = false">{{ $t('nevermind') }}</v-btn>
          <v-btn :loading="converting" color="primary" size="small" variant="tonal" @click="convertToInvoice()">
            Convert
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <StartOrderDocumentDownloadDialog ref="downloadDialogRef"/>
    <SendDocumentDialog
      ref="sendDialogRef"
      :company-name="company?.name"
      :customer-name="customerName"
      :document-label="documentLabel"
      :document-uuid="route.params.uuid as string"
      :has-balance="hasBalance"
      :total-label="totalLabel"
      @sent="onSent"
    />

    <v-dialog v-model="deleteOrderDocumentConfirmDialog" max-width="400">
      <v-card :disabled="orderLoading.deleteOrderDocument">
        <v-card-title>
          Delete Document
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this document?
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn size="small" variant="text" @click="deleteOrderDocumentConfirmDialog = false;">{{ $t('nevermind') }}
          </v-btn>
          <v-btn :loading="orderLoading.deleteOrderDocument" color="error" size="small" @click="deleteOrderDocumentCommit(route.params.uuid as string);">
            {{ $t('delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script lang="ts" setup>
import {computed, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useOrders} from '@/composables/useOrders';
import {useAppSettings} from '@/composables/useAppSettings';
import {copyTextToClipboard} from '@/helpers';
import {push} from 'notivue';
import axios from 'axios';
import StartOrderDocumentDownloadDialog from '@/components/dashboard/dialogs/StartOrderDocumentDownloadDialog.vue';
import SendDocumentDialog from '@/components/dashboard/dialogs/SendDocumentDialog.vue';
import {useCompany} from '@/composables/useCompany';
import {useMoneyFormatter} from '@/composables/useMoneyFormatter';
import type {OrderDocumentType} from '@/types/order';

const downloadDialogRef = ref<InstanceType<typeof StartOrderDocumentDownloadDialog> | null>(null);
const sendDialogRef = ref<InstanceType<typeof SendDocumentDialog> | null>(null);

const route = useRoute();
const router = useRouter();
const {orderDocument, orderLoading, updateOrderDocument, deleteOrderDocument, getOrderDocument} = useOrders();
const {device} = useAppSettings();
const {company} = useCompany();
const {formatMoneyDisplayV2} = useMoneyFormatter();

// Reactive state
const deleteOrderDocumentConfirmDialog = ref(false);
const convertDialog = ref(false);
const converting = ref(false);

// Computed properties
const orderBookingId = computed<string>(() => orderDocument.value?.order?.b_id);
const documentType = computed<OrderDocumentType['type']>(() => orderDocument.value?.type);
const documentCanBeFinalized = computed(() =>
  documentType.value === 'Document::Invoice' && !orderDocument.value?.finalized
);
const hasCustomer = computed(() => Boolean(orderDocument.value?.customer_id));
const customerName = computed<string | null>(() => {
  const c = orderDocument.value?.customer as { first_name?: string; last_name?: string } | undefined;
  const name = [c?.first_name, c?.last_name].filter(Boolean).join(' ').trim();
  return name || null;
});
const documentLabel = computed(() => {
  const kind = documentType.value === 'Document::Quote' ? 'Quote' : 'Invoice';
  return orderDocument.value?.number ? `${kind} #${orderDocument.value.number}` : kind;
});
const hasBalance = computed(() => documentType.value === 'Document::Invoice' && Number(orderDocument.value?.to_be_paid_in_cents ?? 0) > 0);
const totalLabel = computed(() => {
  const cents = Number(orderDocument.value?.grand_total_with_tax_in_cents ?? 0);
  return cents > 0 ? formatMoneyDisplayV2(cents) : null;
});

const onSent = (): void => {
  const uuid = orderDocument.value?.uuid;
  if (uuid) {
    void getOrderDocument(uuid);
  }
};

const statusChip = computed<{ label: string; color: string } | null>(() => {
  if (!orderDocument.value) {
    return null;
  }

  if (documentType.value === 'Document::Quote') {
    return {label: 'Quote', color: 'info'};
  }

  if (orderDocument.value.finalized) {
    return {label: 'Finalized', color: 'success'};
  }

  return {label: 'Draft', color: 'warning'};
});

// Methods
const finalizeDocumentInit = () => {
  const uuid = orderDocument.value?.uuid;
  if (!uuid) {
    console.warn('No document UUID found, cannot finalize.');
    return;
  }

  updateOrderDocument({
    documentUuid: uuid,
    attributes: {
      finalize: true,
    },
  });
};

const deleteOrderDocumentInit = () => {
  deleteOrderDocumentConfirmDialog.value = true;
};

const deleteOrderDocumentCommit = async (uuid: string) => {
  console.log('deleteOrderDocumentCommit');
  try {
    await deleteOrderDocument(uuid);
    await router.push('/documents');
  } catch (error) {
    console.error('Failed to delete document:', error);
  }
};

// Print opens the same server-rendered PDF that Download produces, so the
// printed page is never missing fonts, logos or the paid stamp and both
// buttons give identical output.
const printElem = () => {
  downloadDialogRef.value?.openDialog(route.params.uuid as string, 'print');
};

const downloadOrderDocumentInit = () => {
  console.log('downloadOrderDocumentInit');
  downloadDialogRef.value?.openDialog(route.params.uuid as string);
};

async function convertToInvoice(): Promise<void> {
  const uuid = orderDocument.value?.uuid;
  if (!uuid) {
    return;
  }

  converting.value = true;
  try {
    const {data} = await axios.post(`/v1/order-documents/${uuid}/convert-to-invoice`);
    convertDialog.value = false;
    push.success({message: `Converted to ${data.document?.title ?? 'invoice'}`});
    await getOrderDocument(uuid);
  } catch (error) {
    console.error(error);
    push.error({message: 'Could not convert this quote'});
  } finally {
    converting.value = false;
  }
}

async function copyPublicLink() {
  const uuid = orderDocument.value?.uuid;
  if (!uuid) {
    return;
  }

  try {
    const {data} = await axios.get(`/v1/order-documents/${uuid}/public-link`);
    if (data.public_url) {
      await copyTextToClipboard(data.public_url);
      push.success({message: 'Invoice link copied to clipboard'});
    }
  } catch (error) {
    console.error(error);
    push.error({message: 'Failed to copy invoice link'});
  }
}
</script>


<style scoped>
.document-actions {
  min-width: 0;
}
</style>
